import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { CalendarDaysIcon } from '@heroicons/react/24/outline'

import { Popover } from '@elements/popover'
import { StatusSymbol } from '@elements/status-symbol'
import { Calendar, CalendarProps } from '@elements/calendar'
import { useFormattedDate } from '@hooks/dates'
import { usePopover } from '@hooks/popover'
import { nanoid } from '@utils/strings'
import { Statused } from 'types/ui'

export type DatePickerProps = Omit<
  CalendarProps,
  'date' | 'onSelect' | 'bordered'
> &
  Statused & {
    id?: string
    name?: string
    value: CalendarProps['date']
    onChange: CalendarProps['onSelect']
    displayFormat?: string
    placeholder?: string
    disabled?: boolean
  }

export function DatePicker({
  id = nanoid(),
  name,
  value,
  onChange,
  minDate,
  maxDate,
  error,
  warning,
  success,
  disabled,
  displayFormat = 'M/d/yyyy',
  placeholder = 'Pick a date',
}: DatePickerProps) {
  const containerId = useRef(`date-picker-${id}`)
  const buttonId = useRef(id || `cal-button-${id}`)
  const valueDate = value ? new Date(value).toISOString() : null
  const displayDate = useFormattedDate(valueDate, displayFormat)

  const popover = usePopover({
    placement: 'bottom',
    flip: true,
    arrow: true,
    closeable: true,
    openOnAnchorHover: false,
    closeOnAnchorHoverExit: false,
    toggleOpenOnAnchorClick: false,
    openOnFocus: false,
    closeOnBlur: false,
    offset: [0, 12],
  })

  const popoverVisible = popover.props.visible

  const handleChange = useCallback(
    (val?: string | number | null) => {
      onChange?.(val)
      popover.hide()
      document.getElementById(buttonId.current)?.focus()
    },
    [onChange, popover]
  )

  const handleButtonClick = useCallback(() => {
    popoverVisible ? popover.hide() : popover.show()
  }, [popover, popoverVisible])

  const buttonClassname = useMemo(
    () =>
      classNames(
        'input-styles cursor-pointer text-base text-basic py-[5px] px-3',
        'w-full flex items-center justify-between space-x-3',
        'ring-1 focus:ring-offset-2',
        {
          'state-success': success && !(error || warning),
          'state-warning': !error && warning,
          'state-error': error,
          'ring-error focus:ring-error': popoverVisible && error,
          'ring-warning focus:ring-warning':
            popoverVisible && warning && !error,
          'ring-success focus:ring-success':
            popoverVisible && success && !(error || warning),
          'ring-primary-500 focus:ring-primary-500':
            popoverVisible && !(success || error || warning),
          'ring-transparent focus:ring-primary-500': !popoverVisible,
        }
      ),
    [error, popoverVisible, success, warning]
  )

  useEffect(() => {
    const detectClickOutside = (e: MouseEvent) => {
      const el = e.target as HTMLElement
      const closest = el.closest(`#${containerId.current}`)
      if (!closest) popover.hide()
    }

    if (popoverVisible) document.addEventListener('click', detectClickOutside)

    return () => {
      if (popoverVisible)
        document.removeEventListener('click', detectClickOutside)
    }
  }, [popover, popoverVisible])

  return (
    <div className='relative w-full' id={containerId.current}>
      <button
        type='button'
        id={buttonId.current}
        ref={popover.setAnchorElement}
        onClick={handleButtonClick}
        disabled={disabled}
        className={buttonClassname}
      >
        <CalendarDaysIcon className='w-4 min-h-[16px] min-w-[16px] h-4 stroke-2' />
        <span
          className={classNames({
            'text-zinc-500 font-thin disabled:text-zinc-400': !displayDate,
            'whitespace-nowrap': !!displayDate,
          })}
        >
          {displayDate ?? placeholder}
        </span>
        <div className='w-4 h-4'>
          <StatusSymbol error={error} warning={warning} success={success} />
        </div>
      </button>
      <Popover {...popover.props} unmounting>
        <Calendar
          date={value}
          onSelect={handleChange}
          minDate={minDate}
          maxDate={maxDate}
          bordered={false}
        />
      </Popover>
      <input
        name={name}
        value={value ?? undefined}
        className='hidden'
        onChange={({ target }) => handleChange(target.value)}
      />
    </div>
  )
}
